import { useState } from 'react';
import { Order } from '@/data/mockData';
import { SortField, SortDirection } from '@/hooks/useOrdersSort';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronUp, ChevronDown, CheckCircle, Clock, X, FileText, FilePenLine, ExternalLink, Info } from 'lucide-react';
import { OrderInfoDialog } from '@/components/OrderInfoDialog';

interface OrdersTableProps {
  orders: Order[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onDownloadQuote: (order: Order) => void;
  onConfirmDelivery: (order: Order) => void;
}

const SortIcon = ({ field, currentField, direction }: { field: SortField; currentField: SortField; direction: SortDirection }) => {
  if (currentField !== field) return <ChevronUp className="w-4 h-4 opacity-30" />;
  return direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
};

const getStatusIcon = (status: Order['status']) => {
  const base = "flex h-8 w-8 items-center justify-center rounded-full";

  switch (status) {
    case 'SUCCESS':
      return (
        <div className={`${base} bg-success/15 text-success`}>
          <CheckCircle className="h-4 w-4" />
        </div>
      );
    case 'RUNNING':
      return (
        <div className={`${base} border border-ring/40 text-ring`}>
          <Clock className="h-4 w-4 animate-pulse" />
        </div>
      );
    case 'ERROR':
      return (
        <div className={`${base} bg-destructive/15 text-destructive`}>
          <X className="h-4 w-4" />
        </div>
      );
    case 'QUOTE_REQUESTED':
      return (
        <div className={`${base} bg-secondary/30 text-primary`}>
          <FilePenLine className="h-4 w-4" />
        </div>
      );
    case 'NEW_REQUEST':
      return (
        <div className={`${base} border border-ring/40 text-ring`}>
          <Clock className="h-4 w-4 animate-pulse" />
        </div>
      );
    default:
      return null;
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toISOString().split('T')[0];
};

const formatPrice = (price: number) => {
  return `${price.toLocaleString('fr-FR')} €`;
};

const formatType = (type: string) => {
  switch (type) {
    case 'Email':
      return 'Emails';
    case 'Mobile':
      return 'Mobiles';
    case 'CRM ID':
      return 'CRM IDs';
    default:
      return type;
  }
};

// Libellé lisible du id_type brut de l'API (selon emails/phones et l'opérateur OR/AND).
const formatIdType = (idType?: string) => {
  switch (idType) {
    case 'emails':
      return 'Emails';
    case 'phones':
      return 'Mobiles';
    case 'emails OR phones':
      return 'Emails ou Mobiles';
    case 'emails AND phones':
      return 'Emails et Mobiles';
    default:
      return idType ?? '—';
  }
};

// En mode population, une demande peut combiner plusieurs types de contact.
// Si les types sont hétérogènes, on affiche « Mixte » (le détail par population
// reste consultable via le bouton info) plutôt qu'une énumération empilée.
const formatOrderType = (order: Order) => {
  if (order.populationRequests && order.populationRequests.length > 0) {
    const distinct = Array.from(
      new Set(order.populationRequests.map((p) => p.idType))
    );
    return distinct.length > 1 ? 'Mixte' : formatIdType(distinct[0]);
  }
  // Demande sans population : libellé basé sur le id_type brut si disponible.
  return order.idType ? formatIdType(order.idType) : formatType(order.type);
};

const formatDuration = (duration: Order['duration']) => {
  if (!duration) return '—';
  return `${duration} jours`;
};

export const OrdersTable = ({
  orders,
  sortField,
  sortDirection,
  onSort,
  onDownloadQuote,
  onConfirmDelivery,
}: OrdersTableProps) => {
  const [infoOrder, setInfoOrder] = useState<Order | null>(null);

  const handleInfoClick = (order: Order) => {
    setInfoOrder(order);
  };

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-accent/30">
            <tr>
              <th 
                className="text-left py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('id')}
              >
                <div className="flex items-center gap-2">
                  ID de requête
                  <SortIcon field="id" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th 
                className="text-left py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('requestName')}
              >
                <div className="flex items-center gap-2">
                  DEMANDE
                  <SortIcon field="requestName" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th 
                className="text-center py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('duration')}
              >
                <div className="flex items-center justify-center gap-2">
                  PÉRIODE
                  <SortIcon field="duration" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th 
                className="text-center py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('volume')}
              >
                <div className="flex items-center justify-center gap-2">
                  Volume
                  <SortIcon field="volume" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th className="text-center py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide">
                Type
              </th>
              <th 
                className="text-center py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('price')}
              >
                <div className="flex items-center justify-center gap-2">
                  Prix
                  <SortIcon field="price" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th 
                className="text-left py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('date')}
              >
                <div className="flex items-center gap-2">
                  Date
                  <SortIcon field="date" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th 
                className="text-left py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('owner')}
              >
                <div className="flex items-center gap-2">
                  Propriétaire
                  <SortIcon field="owner" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th className="text-center py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide">
                Devis
              </th>
              <th 
                className="text-center py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onSort('status')}
              >
                <div className="flex items-center justify-center gap-2">
                  Statut
                  <SortIcon field="status" currentField={sortField} direction={sortDirection} />
                </div>
              </th>
              <th className="text-center py-4 px-6 font-medium text-muted-foreground text-sm uppercase tracking-wide">
                RÉSULTAT
              </th>
            </tr>
          </thead>
          <tbody className="bg-card">
            {orders.map((order, index) => (
              <tr 
                key={order.id} 
                className={`border-b border-border/50 last:border-b-0 hover:bg-accent/50 transition-colors ${
                  index % 2 === 0 ? 'bg-accent/20' : 'bg-card'
                }`}
              >
                <td className="py-4 px-6 font-mono text-sm text-muted-foreground">{order.id}</td>
                <td className="py-4 px-6 font-medium">
                  <div className="flex items-center gap-2">
                    <span>{order.requestName}</span>
                    <button
                      onClick={() => handleInfoClick(order)}
                      className="text-primary hover:text-primary/90 transition-colors"
                      title="Voir les détails de la demande"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                </td>
                <td className="py-4 px-6 text-center">{formatDuration(order.duration)}</td>
                <td className="py-4 px-6 text-center">{order.volume.toLocaleString()}</td>
                <td className="py-4 px-6 text-center text-sm whitespace-nowrap">{formatOrderType(order)}</td>
                <td className="py-4 px-6 text-center">
                  <span className="font-bold text-success">
                    {formatPrice(order.price)}
                  </span>
                </td>
                <td className="py-4 px-6">{formatDate(order.date)}</td>
                <td className="py-4 px-6 text-muted-foreground">{order.owner}</td>
                <td className="py-4 px-6">
                  {order.status !== 'ERROR' && (
                    <div className="flex items-center justify-center">
                      <button
                        onClick={() => onDownloadQuote(order)}
                        className="text-primary hover:text-primary/90 transition-colors"
                        title="Télécharger le devis"
                      >
                        <FileText className="h-6 w-6" />
                      </button>
                    </div>
                  )}
                </td>
                <td className="py-4 px-6">
                  {order.status === 'QUOTE_REQUESTED' ? (
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-xs font-medium text-muted-foreground">Devis demandé</span>
                      <Button
                        size="sm"
                        className="w-full justify-center gap-2"
                        onClick={() => onConfirmDelivery(order)}
                        title="Cliquez pour confirmer la livraison"
                      >
                        Livraison →
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      {getStatusIcon(order.status)}
                    </div>
                  )}
                </td>
                <td className="py-4 px-6">
                  {order.status === 'SUCCESS' && order.resultUrl ? (
                    <div className="flex items-center justify-center">
                      <a
                        href={order.resultUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/90 transition-colors"
                        title="Voir le résultat"
                      >
                        <ExternalLink className="h-6 w-6" />
                      </a>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {orders.map((order) => (
          <Card key={order.id} className="p-4">
            <div className="flex justify-between items-start mb-3">
              <span className="font-mono text-sm text-muted-foreground">{order.id}</span>
              {getStatusIcon(order.status)}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="font-medium">{order.requestName}</p>
                <button
                  onClick={() => handleInfoClick(order)}
                  className="text-primary hover:text-primary/90 transition-colors"
                  title="Voir les détails de la demande"
                >
                  <Info className="h-4 w-4" />
                </button>
              </div>
              <div className="flex justify-between text-sm">
                <span>Période: {formatDuration(order.duration)}</span>
                <span>Volume: {order.volume.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-success">{formatPrice(order.price)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{formatDate(order.date)}</span>
                <span>{order.owner}</span>
              </div>
              {order.status === 'SUCCESS' && order.resultUrl && (
                <div className="flex items-center gap-2 text-sm">
                  <a
                    href={order.resultUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:text-primary/90 transition-colors"
                    title="Voir le résultat"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Résultat</span>
                  </a>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <OrderInfoDialog
        order={infoOrder}
        onOpenChange={(open) => !open && setInfoOrder(null)}
      />
    </>
  );
};

